import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import SlidePanel from '../shared/SlidePanel';
import { pricingService } from '../../_services/pricing.service';
import { openUpgrade } from '../pricing/PricingBits';
import { BAILEY_PLUS } from '../pricing/usageRules';
import { isOwnerHere } from './access';
import { AGENT_VISIBLE, PRICING_V2_VISIBLE } from '../../config/featureFlags';
import { hapticError, hapticTick } from '../../_utils/haptics';

/**
 * Team > Permissions: who on the employed team sees Bailey (pricing
 * 2026-10-07). Same shape as Marketing › Salon controlled marketing: a
 * compact card with the count, and "Choose who" opens a side panel with a
 * switch per person. Off for everyone until the owner switches a person on,
 * and only with Bailey Plus on this location. What they can ask is still
 * their role's. Self-employed stylists aren't listed: they have their own.
 * Owner only; renders nothing for anyone else or if the API refuses.
 * Opened straight to the panel with location.state.openBaileyTeam.
 */
export default function BaileyTeam() {
  const location = useLocation();
  const [state, setState] = useState({ loading: true, error: '', plus: false, staff: [], hidden: false });
  const [busy, setBusy] = useState('');
  const [open, setOpen] = useState(!!(location && location.state && location.state.openBaileyTeam));

  const load = useCallback(async () => {
    try {
      const r = await pricingService.baileyStaff();
      setState({ loading: false, error: '', plus: !!(r && r.plus), staff: (r && r.staff) || [], hidden: false });
    } catch (e) {
      setState((s) => ({ ...s, loading: false, hidden: e && e.status === 403, error: e && e.status === 403 ? '' : 'Could not load your team.' }));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!PRICING_V2_VISIBLE || !AGENT_VISIBLE || !isOwnerHere() || state.hidden) return null;

  const toggle = async (p) => {
    if (!state.plus || busy) return;
    hapticTick();
    setBusy(p.userId);
    const was = state.staff;
    setState((s) => ({ ...s, error: '', staff: s.staff.map((x) => (x.userId === p.userId ? { ...x, enabled: !p.enabled, active: !p.enabled } : x)) }));
    try {
      const r = await pricingService.setBaileyStaff(p.userId, !p.enabled);
      if (r && r.staff) setState((s) => ({ ...s, plus: !!r.plus, staff: r.staff }));
    } catch (e) {
      hapticError();
      setState((s) => ({ ...s, staff: was, error: e && e.code === 'bailey_plus_required' ? `Bailey for the team comes with ${BAILEY_PLUS}.` : 'That didn’t save. Try again.' }));
    }
    setBusy('');
  };
  const on = state.staff.filter((p) => p.enabled).length;

  return (
    <div className="settings-card">
      <div className="settings-card-header perm-card-header">
        <div>
          <h2 className="perm-bt-title">
            Bailey for your team
            {!state.loading && state.plus && state.staff.length > 0 && <span className="perm-bt-chip">{on} of {state.staff.length} on</span>}
          </h2>
          <p>
            {state.plus
              ? 'Switch Bailey on for the people who should have it. They can only ask about what their role lets them see, and their questions come out of this location’s monthly questions.'
              : `Give your employed team Bailey, person by person. It comes with ${BAILEY_PLUS}.`}
          </p>
        </div>
        {!state.loading && (state.plus
          ? (state.staff.length > 0 && <button type="button" className="perm-bt-btn" onClick={() => { hapticTick(); setOpen(true); }}>Choose who</button>)
          : <button type="button" className="bu-inline-save" onClick={() => { hapticTick(); openUpgrade({ from: 'team-bailey' }); }}>Get {BAILEY_PLUS}</button>)}
      </div>
      {state.loading && <p className="perm-intro perm-loading">Loading your team…</p>}
      {state.error && !open && <p className="perm-intro perm-error">{state.error}</p>}
      {!state.loading && state.plus && !state.staff.length && <p className="perm-intro">No employed team members here yet.</p>}

      {open && (
        <SlidePanel title="Bailey for your team" width={420} onClose={() => setOpen(false)}>
          <div className="sp-form">
            <p className="sp-hint" style={{ marginTop: 0 }}>
              Bailey is off for your employed team until you switch it on here. They can only ask about what their role lets them see. Self-employed stylists have their own Bailey.
            </p>
            {state.error && <p className="perm-error" role="alert">{state.error}</p>}
            {state.staff.map((p) => {
              const checked = state.plus && p.enabled;
              return (
                <div key={p.userId} className="perm-bt-row">
                  <span className="perm-bt-name">{p.name || 'Team member'}</span>
                  <span className={`perm-bt-state${checked ? ' is-on' : ''}`}>{checked ? 'On' : 'Off'}</span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={checked}
                    aria-label={`Bailey for ${p.name || 'team member'}`}
                    disabled={!state.plus || busy === p.userId}
                    className={`perm-bt-switch${checked ? ' is-on' : ''}`}
                    onClick={() => toggle(p)}
                  >
                    <span />
                  </button>
                </div>
              );
            })}
          </div>
        </SlidePanel>
      )}
    </div>
  );
}
